/**
 * client/src/lib/push.ts
 *
 * Web Push subscribe / unsubscribe helpers. Used by the Settings page when the
 * sister opts into reminders.
 *
 * These previously called `/api/push/*` on the Express server, which was never
 * deployed -- every path was swallowed by the SPA rewrite. They now call the
 * Convex functions in convex/push.ts.
 *
 * KNOWN INCOMPLETE, stated plainly so no UI claims otherwise:
 * `convex/push.ts:send` is still a placeholder that does not deliver a
 * notification. Subscribing genuinely persists a subscription row, but
 * nothing sends to it yet. `sendTestPush` reports that honestly instead of
 * returning a fabricated success. Callers must surface `ok:false` to the user.
 */

import { track } from "@/lib/telemetry";
import { api } from "../../../convex/_generated/api";
import { getConvexClient } from "@/lib/convexClient";

export interface PushResult {
  ok: boolean;
  reason?: string;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

/** True only when the browser has actually granted notification permission. */
export function hasPushPermission(): boolean {
  return isPushSupported() && Notification.permission === "granted";
}

/** True only when a real PushSubscription exists for this device. */
export async function isSubscribedToPush(): Promise<boolean> {
  if (!hasPushPermission()) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    return (await reg.pushManager.getSubscription()) != null;
  } catch (err) {
    console.error("[push] could not read existing subscription", err);
    return false;
  }
}

export async function subscribeToPush(): Promise<PushResult> {
  if (!isPushSupported()) {
    return { ok: false, reason: "Push is not supported in this browser." };
  }

  const publicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
  if (!publicKey) {
    // Fail loudly at the source rather than subscribing against a bad key.
    console.error("[push] VITE_VAPID_PUBLIC_KEY is not set; cannot subscribe");
    return { ok: false, reason: "Push is not configured on this deployment." };
  }

  const client = getConvexClient();
  if (!client) {
    console.error("[push] Convex client unavailable; cannot save subscription");
    return { ok: false, reason: "Can't reach the server right now." };
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    track("push_permission_denied", { permission });
    return { ok: false, reason: "Permission denied." };
  }

  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });

    const json = sub.toJSON() as {
      endpoint?: string;
      keys?: { p256dh?: string; auth?: string };
    };
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
      throw new Error("browser returned an incomplete push subscription");
    }

    await client.mutation(api.push.saveSubscription, {
      userId: "",            // ignored server-side; identity comes from auth
      endpoint: json.endpoint,
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
    });

    track("push_subscribed");
    return { ok: true };
  } catch (err) {
    console.error("[push] subscribe failed", err);
    track("push_subscribe_error", { message: (err as Error)?.message });
    return { ok: false, reason: "We couldn't turn on reminders. Please try again." };
  }
}

export async function unsubscribeFromPush(): Promise<PushResult> {
  if (!isPushSupported()) {
    return { ok: false, reason: "Push is not supported in this browser." };
  }
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return { ok: true };

    const client = getConvexClient();
    if (client) {
      // Drop the server row first; if this throws we surface it rather than
      // unsubscribing locally and leaving an orphan row that still receives.
      await client.mutation(api.push.removeSubscription, { endpoint: sub.endpoint });
    } else {
      console.error("[push] Convex client unavailable; server row not removed");
    }

    await sub.unsubscribe();
    track("push_unsubscribed");
    return { ok: true };
  } catch (err) {
    console.error("[push] unsubscribe failed", err);
    track("push_unsubscribe_error", { message: (err as Error)?.message });
    return { ok: false, reason: "We couldn't turn off reminders. Please try again." };
  }
}

export async function sendTestPush(): Promise<PushResult> {
  const client = getConvexClient();
  if (!client) return { ok: false, reason: "Can't reach the server right now." };
  try {
    const result = await client.action(api.push.send, {
      userId: "",
      title: "Move Nourish Flourish",
      body: "Test reminder — your notifications are working.",
    });
    // convex/push.ts:send is a placeholder today. Report what it actually
    // returned instead of assuming success.
    if (!result?.sent) {
      return { ok: false, reason: result?.reason ?? "Push delivery is not configured yet." };
    }
    return { ok: true };
  } catch (err) {
    console.error("[push] test send failed", err);
    return { ok: false, reason: "We couldn't send a test notification." };
  }
}
